// Overview: what stands out, the headline numbers, how a case usually goes and where its time goes,
// within the current case scope.
import { ArrowDown, ArrowRight, ChevronDown, MessageCircleQuestion } from "lucide-react";
// biome-ignore lint/style/useImportType: this bundle compiles JSX with the classic React runtime.
import React, { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Process } from "../../api/types";
import { formatDuration } from "../../utils";
import { getAllocationColor } from "../analytics/palette";
import { useResponsiveWidth } from "../../utils/useResponsiveWidth";
import ProcessNoticedCard, { type NoticedFinding } from "./ProcessNoticedCard";
import type { OpenTab } from "./ProcessPanelTabs";
import { appIconUri } from "./appIcon";
import { caseLoops } from "./processCaseNotes";
import { median } from "./processDurationStats";
import {
  type EdgeSelection,
  type ProcessEdge,
  type ProcessGraphNode,
  type ProcessInstance,
  type StepNamesById,
  type StepTimeStats,
  type UsualStep,
  caseCount,
  caseElapsedSeconds,
  caseTimeSplit,
  rankedTransitionWaitTotals,
  totalDuration,
  usualSteps,
} from "./processGraphModel";
import { noticedFindings } from "./processNoticed";
import { AskWhyButton, KpiTiles, PanelSection, ShowAllButton } from "./processSidebarUi";

const WORK_COLOR = getAllocationColor(0);
const WAIT_COLOR = getAllocationColor(1);
// Rows a list shows until it is opened.
const TOP_ROWS = 5;
// A row's bar shows only where the list has room for it beside the label.
const BAR_TRACK_CLASS = "hidden h-2 overflow-hidden rounded-full bg-muted-surface @lg:block";
const ASK_POSITION_CLASS = "right-2 top-1/2 -translate-y-1/2 text-xs";
// The usual steps' boxes, and the room between them that their arrows run through.
const FLOW_BOX_MIN_WIDTH = 160;
const FLOW_BOX_HEIGHT = 48;
const FLOW_GAP_X = 28;
const FLOW_GAP_Y = 32;
const FLOW_CORNER_RADIUS = 7;
// Space between an arrow's ends and the boxes it joins.
const FLOW_ARROW_INSET = 3;

// Shared widths align the count/average and combined total/share in both breakdowns.
const TIME_READINGS_CLASS = "grid grid-cols-[3rem_7rem] gap-x-2 text-right";
const timeShare = (share: number) =>
  share > 0 && share < 0.01 ? "<1%" : `${Math.round(share * 100)}%`;

const TimeReadingHeader = ({
  first,
  className = "px-2",
}: { first: string; className?: string }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`mb-0.5 flex justify-end text-[10px] font-medium uppercase tracking-wide text-text-dim ${className}`}
    >
      <div className={TIME_READINGS_CLASS}>
        <span>{first}</span>
        <span>{t("processPanel.columnTotal")}</span>
      </div>
    </div>
  );
};

const TimeReadings = ({
  first,
  totalSeconds,
  share,
}: {
  first: React.ReactNode;
  totalSeconds: number;
  share: number;
}) => (
  <span className={`${TIME_READINGS_CLASS} whitespace-nowrap text-xs tabular-nums text-text-mid`}>
    <span>{first}</span>
    <span>
      {formatDuration(totalSeconds)} <span className="text-text-dim">({timeShare(share)})</span>
    </span>
  </span>
);

/**
 * A step's name for this tab's lists. Two steps can share a name (the same activity done in two
 * apps); the app is what tells them apart on the map, so it tells them apart here.
 */
const useStepName = (nameById: StepNamesById, appById: Map<string, string | null>) => {
  const sharedNames = useMemo(() => {
    const seen = new Set<string>();
    const shared = new Set<string>();
    for (const name of nameById.values()) {
      if (seen.has(name)) shared.add(name);
      seen.add(name);
    }
    return shared;
  }, [nameById]);
  return (id: string) => {
    const name = nameById.get(id) ?? id;
    const app = appById.get(id);
    return sharedNames.has(name) && app ? `${name} (${app})` : name;
  };
};

/**
 * One row of a ranked list, on one line: what it is, a bar for its share and its readings. The bar
 * takes a column between the label and the readings where the list's container has room, as the
 * dashboard's process list does; narrower, the label gets that room and the share reads as text.
 */
const ShareRow = ({
  label,
  title,
  share,
  color,
  reading,
  barTestId,
  onClick,
  ask,
}: {
  label: React.ReactNode;
  title: string;
  share: number;
  color: string;
  reading: React.ReactNode;
  barTestId?: string;
  onClick: () => void;
  ask?: { label: string; onClick: () => void };
}) => (
  <div className="group relative rounded-lg text-sm transition hover:bg-surface-strong">
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-lg px-2 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 @lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] @lg:gap-x-4"
    >
      <span className="min-w-0 truncate whitespace-nowrap font-medium leading-snug text-text">
        {label}
      </span>
      <span aria-hidden="true" className={BAR_TRACK_CLASS}>
        <span
          data-testid={barTestId}
          className="block h-full rounded-full"
          style={{ width: `${share * 100}%`, backgroundColor: color }}
        />
      </span>
      <span className={ask ? "transition-opacity group-hover:opacity-0" : undefined}>
        {reading}
      </span>
    </button>
    {ask && <AskWhyButton label={ask.label} onClick={ask.onClick} className={ASK_POSITION_CLASS} />}
  </div>
);

export type TransitionWaitAskContext = {
  from: { id: string; name: string; iconUri: string | null };
  to: { id: string; name: string; iconUri: string | null };
  averageWaitSeconds: number;
  transitionsCount: number;
};

type TransitionWaitRowData = TransitionWaitAskContext & { totalWaitSeconds: number; share: number };

/**
 * One wait between two steps: the route, its share of all waiting and the average pause.
 * The readings stay beside the route as the step names switch between stacked and horizontal.
 */
const TransitionWaitRow = ({
  transition,
  onSelect,
  onAsk,
}: {
  transition: TransitionWaitRowData;
  onSelect: (selection: EdgeSelection) => void;
  onAsk: (transition: TransitionWaitAskContext) => void;
}) => {
  const { t } = useTranslation();
  const icon = (step: TransitionWaitAskContext["from"]) =>
    step.iconUri ? (
      <img src={step.iconUri} alt="" className="h-4 w-4 shrink-0 object-contain" />
    ) : (
      <span aria-hidden="true" className="h-4 w-4 shrink-0" />
    );
  return (
    <div className="group grid grid-cols-[minmax(0,1fr)_auto] items-center rounded-lg text-sm transition hover:bg-surface-strong focus-within:bg-surface-strong">
      <button
        type="button"
        onClick={() =>
          onSelect({
            kind: "edge",
            from: transition.from.id,
            to: transition.to.id,
          })
        }
        title={`${transition.from.name} → ${transition.to.name}`}
        aria-label={t("focus_transition", {
          from: transition.from.name,
          to: transition.to.name,
        })}
        className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-lg px-2 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <span className="grid min-w-0 items-center justify-items-center gap-1 text-center leading-5 @lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] @lg:gap-x-3 @lg:text-left">
          <span className="flex min-w-0 items-center justify-center gap-2 @lg:w-full @lg:justify-start">
            {icon(transition.from)}
            <span className="min-w-0 break-words font-medium text-text">
              {transition.from.name}
            </span>
          </span>
          <ArrowDown
            aria-hidden="true"
            className="h-5 w-5 text-text-dim @lg:hidden"
            strokeWidth={1.5}
          />
          <ArrowRight
            aria-hidden="true"
            className="hidden h-5 w-5 text-text-dim @lg:block"
            strokeWidth={1.5}
          />
          <span className="flex min-w-0 items-center justify-center gap-2 @lg:w-full @lg:justify-start">
            {icon(transition.to)}
            <span className="min-w-0 break-words text-text-mid">{transition.to.name}</span>
          </span>
        </span>
        <TimeReadings
          first={formatDuration(transition.averageWaitSeconds)}
          totalSeconds={transition.totalWaitSeconds}
          share={transition.share}
        />
      </button>
      <button
        type="button"
        aria-label={t("processPanel.askAboutGap", {
          from: transition.from.name,
          to: transition.to.name,
        })}
        title={`${t("processPanel.askAboutGap", {
          from: transition.from.name,
          to: transition.to.name,
        })} · ${t("chat_opens_sidebar")}`}
        onClick={() => onAsk(transition)}
        className="mr-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-text-dim opacity-0 transition hover:text-text group-hover:opacity-100 group-focus-within:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
};

const WaitRows = ({
  waits,
  onSelect,
  onAsk,
}: {
  waits: TransitionWaitRowData[];
  onSelect: (selection: EdgeSelection) => void;
  onAsk: (transition: TransitionWaitAskContext) => void;
}) => (
  <ul className="@container space-y-0.5">
    {waits.map(transition => (
      <li key={`${transition.from.id}->${transition.to.id}`}>
        <TransitionWaitRow transition={transition} onSelect={onSelect} onAsk={onAsk} />
      </li>
    ))}
  </ul>
);

type FlowPoint = { x: number; y: number };

/**
 * Where each of the usual steps' boxes sits. A run of steps joined by arrows starts a line of its
 * own, and so do the steps after one, so each run reads apart from the others. Each part takes as
 * many steps to a line as fit, spread evenly over the lines it needs, every line reading left to
 * right like text.
 */
const flowLayout = (steps: UsualStep[], width: number) => {
  const fit = Math.max(1, Math.floor((width + FLOW_GAP_X) / (FLOW_BOX_MIN_WIDTH + FLOW_GAP_X)));
  const joined = (index: number) =>
    (steps[index]?.leadsToNext ?? false) || (steps[index - 1]?.leadsToNext ?? false);
  // How many steps each part has: a new part starts wherever a run begins or ends.
  const parts: number[] = [];
  steps.forEach((step, index) => {
    const startsPart =
      index === 0 || (!steps[index - 1].leadsToNext && (joined(index - 1) || joined(index)));
    if (startsPart) parts.push(1);
    else parts[parts.length - 1] += 1;
  });
  const perLine = parts.map(count => Math.ceil(count / Math.ceil(count / fit)));
  const columns = Math.max(1, ...perLine);
  const boxWidth = (width - (columns - 1) * FLOW_GAP_X) / columns;
  const boxes: FlowPoint[] = [];
  let line = 0;
  parts.forEach((count, part) => {
    for (let index = 0; index < count; index += 1) {
      boxes.push({
        x: (index % perLine[part]) * (boxWidth + FLOW_GAP_X),
        y: (line + Math.floor(index / perLine[part])) * (FLOW_BOX_HEIGHT + FLOW_GAP_Y),
      });
    }
    line += Math.ceil(count / perLine[part]);
  });
  return { boxWidth, height: line * FLOW_BOX_HEIGHT + (line - 1) * FLOW_GAP_Y, boxes };
};

/**
 * The arrow from one box to the next: straight along a line, or, where the steps wrap, down from
 * the line's last box and round into the top of the next line's first.
 */
const flowArrow = (from: FlowPoint, to: FlowPoint, boxWidth: number) => {
  if (from.y === to.y) {
    const y = from.y + FLOW_BOX_HEIGHT / 2;
    return `M${from.x + boxWidth + FLOW_ARROW_INSET} ${y}H${to.x - FLOW_ARROW_INSET}m-5-4 5 4-5 4`;
  }
  const startX = from.x + boxWidth / 2;
  const endX = to.x + boxWidth / 2;
  const turnY = from.y + FLOW_BOX_HEIGHT + FLOW_GAP_Y / 2;
  const radius = Math.min(FLOW_CORNER_RADIUS, (startX - endX) / 2);
  const turn =
    radius > 0
      ? `V${turnY - radius}Q${startX} ${turnY} ${startX - radius} ${turnY}` +
        `H${endX + radius}Q${endX} ${turnY} ${endX} ${turnY + radius}`
      : "";
  return `M${startX} ${from.y + FLOW_BOX_HEIGHT + FLOW_ARROW_INSET}${turn}V${to.y - FLOW_ARROW_INSET}m-4-5 4 5 4-5`;
};

/**
 * The usual steps as small boxes with their app's logo, an arrow joining each to the next where
 * most cases doing both agree on their order.
 */
const UsualStepsFlow = ({
  steps,
  stepName,
  onSelect,
}: {
  steps: UsualStep[];
  stepName: (id: string) => string;
  onSelect: (id: string) => void;
}) => {
  const [ref, width] = useResponsiveWidth(640);
  const { boxWidth, height, boxes } = flowLayout(steps, width);
  return (
    <div ref={ref} className="relative" style={{ height }}>
      <svg
        aria-hidden="true"
        className="absolute inset-0 overflow-visible text-text-dim"
        width={width}
        height={height}
        fill="none"
      >
        {steps.map(
          (step, index) =>
            step.leadsToNext && (
              <path
                key={step.node.id}
                d={flowArrow(boxes[index], boxes[index + 1], boxWidth)}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ),
        )}
      </svg>
      <ol>
        {steps.map(({ node }, index) => {
          const iconUri = appIconUri(node.app);
          return (
            <li
              key={node.id}
              className="absolute"
              style={{
                left: boxes[index].x,
                top: boxes[index].y,
                width: boxWidth,
                height: FLOW_BOX_HEIGHT,
              }}
            >
              <button
                type="button"
                onClick={() => onSelect(node.id)}
                title={stepName(node.id)}
                className="flex h-full w-full items-center gap-2 rounded-md border border-outline-strong bg-surface px-2.5 text-left text-xs font-medium leading-tight text-text transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                {iconUri && (
                  <img src={iconUri} alt="" className="h-4 w-4 shrink-0 object-contain" />
                )}
                {/* Without a logo, the app is what tells two steps of the same name apart. */}
                <span className="line-clamp-2 min-w-0">
                  {iconUri ? node.name : stepName(node.id)}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/** A list the section can open under its bar: the work in steps, or the waits between them. */
type TimeView = "work" | "wait";

const Swatch = ({ color }: { color: string }) => (
  <span
    aria-hidden="true"
    className="h-2 w-2 shrink-0 rounded-sm"
    style={{ backgroundColor: color }}
  />
);

/**
 * All the cases' time in one bar: working in steps against waiting between them, read on its own
 * until one of its parts is clicked. Each part opens its list from the bar or from its legend
 * entry; the bar is the pointer's shortcut, the legend the keyboard's. While a list is open the
 * other part dims, tying the list to its color.
 */
const TimeSplit = ({
  workShare,
  view,
  canOpenWaits,
  listId,
  onToggle,
}: {
  workShare: number;
  view: TimeView | null;
  canOpenWaits: boolean;
  listId: string;
  onToggle: (view: TimeView) => void;
}) => {
  const { t } = useTranslation();
  const workPct = Math.round(workShare * 100);
  const parts = (
    [
      {
        view: "work",
        share: workShare,
        color: WORK_COLOR,
        label: t("processPanel.timeWorking", { share: workPct }),
        opens: true,
      },
      {
        view: "wait",
        share: 1 - workShare,
        color: WAIT_COLOR,
        label: t("processPanel.timeWaiting", { share: 100 - workPct }),
        opens: canOpenWaits,
      },
    ] as const
  ).filter(part => part.share > 0);
  return (
    <div className="px-2">
      <div className="flex gap-0.5">
        {parts.map((part, index) => (
          <button
            key={part.view}
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            disabled={!part.opens}
            onClick={() => onToggle(part.view)}
            className="group min-w-0 basis-0 py-1.5 disabled:cursor-default"
            style={{ flexGrow: part.share }}
          >
            <span
              data-testid={`time-split-${part.view}`}
              className={`block h-2.5 transition-opacity ${index === 0 ? "rounded-l-full" : ""} ${index === parts.length - 1 ? "rounded-r-full" : ""} ${view && view !== part.view ? "opacity-30" : ""} ${part.opens ? "group-hover:opacity-80" : ""}`}
              style={{ backgroundColor: part.color }}
            />
          </button>
        ))}
      </div>
      <div className="-mx-1.5 flex justify-between gap-3">
        {parts.map(part => {
          const open = view === part.view;
          return part.opens ? (
            <button
              key={part.view}
              type="button"
              aria-expanded={open}
              aria-controls={listId}
              onClick={() => onToggle(part.view)}
              className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${open ? "bg-surface-strong font-medium text-text" : "text-text-mid"}`}
            >
              <Swatch color={part.color} />
              {part.label}
              <ChevronDown
                aria-hidden="true"
                className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>
          ) : (
            <span
              key={part.view}
              className="inline-flex items-center gap-1.5 px-1.5 py-1 text-xs text-text-mid"
            >
              <Swatch color={part.color} />
              {part.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

/** The waits between steps, named and iconed for their rows, with their share of all waiting. */
const useWaitingTransitions = (
  edges: ProcessEdge[],
  startNodeId: string,
  endNodeId: string,
  steps: ProcessGraphNode[],
  waitSeconds: number,
) =>
  useMemo(() => {
    const nodeById = new Map(steps.map(node => [node.id, node]));
    return rankedTransitionWaitTotals(edges, startNodeId, endNodeId).flatMap(
      (wait): TransitionWaitRowData[] => {
        const fromNode = nodeById.get(wait.from);
        const toNode = nodeById.get(wait.to);
        return fromNode && toNode
          ? [
              {
                from: { id: wait.from, name: fromNode.name, iconUri: appIconUri(fromNode.app) },
                to: { id: wait.to, name: toNode.name, iconUri: appIconUri(toNode.app) },
                averageWaitSeconds: wait.averageWaitSeconds,
                totalWaitSeconds: wait.waitSecondsTotal ?? 0,
                transitionsCount: wait.transitionsCount,
                share:
                  waitSeconds > 0 ? Math.min(1, (wait.waitSecondsTotal ?? 0) / waitSeconds) : 0,
              },
            ]
          : [];
      },
    );
  }, [edges, endNodeId, startNodeId, steps, waitSeconds]);

const ProcessOverview = ({
  process,
  selectedInstances,
  stepStats,
  nameById,
  edges,
  startNodeId,
  endNodeId,
  onSelect,
  onAskTimeSink,
  onAskWaitingTime,
  onSelectTransition,
  onAskNoticed,
  onOpenTab,
}: {
  process: Process;
  selectedInstances: ProcessInstance[];
  stepStats: StepTimeStats;
  nameById: StepNamesById;
  edges: ProcessEdge[];
  startNodeId: string;
  endNodeId: string;
  onSelect: (id: string) => void;
  onAskTimeSink: (step: { id: string; name: string }, sharePct: number) => void;
  onAskWaitingTime: (transition: TransitionWaitAskContext) => void;
  onSelectTransition: (selection: EdgeSelection) => void;
  onAskNoticed: (text: { headline: string; detail: string }) => void;
  onOpenTab: OpenTab;
}) => {
  const { t } = useTranslation();
  const { steps, ranked, shareById, occurrences } = stepStats;
  const usualRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLDivElement>(null);
  // The bar reads on its own; a list opens only once the reader asks for one, at its top entries.
  const [timeView, setTimeView] = useState<TimeView | null>(null);
  const [showAll, setShowAll] = useState(false);
  const timeListId = useId();
  const openTimeView = (view: TimeView | null) => {
    setTimeView(view);
    setShowAll(false);
  };
  const appById = useMemo(() => new Map(steps.map(node => [node.id, node.app ?? null])), [steps]);
  const stepName = useStepName(nameById, appById);
  const findings = useMemo(
    () =>
      noticedFindings({
        instances: selectedInstances,
        stepStats,
        edges,
      }),
    [selectedInstances, stepStats, edges],
  );
  const { workSeconds, waitSeconds } = useMemo(
    () => caseTimeSplit(selectedInstances),
    [selectedInstances],
  );
  const waits = useWaitingTransitions(edges, startNodeId, endNodeId, steps, waitSeconds);
  const usual = useMemo(() => usualSteps(steps, selectedInstances), [steps, selectedInstances]);
  // Medians, so a case left open over a weekend does not set what a typical one looks like.
  const durations = useMemo(
    () => selectedInstances.map(instance => instance.source.avgDuration).sort((a, b) => a - b),
    [selectedInstances],
  );
  const elapsed = useMemo(
    () =>
      selectedInstances
        .flatMap(instance => caseElapsedSeconds(instance) ?? [])
        .sort((a, b) => a - b),
    [selectedInstances],
  );
  const instanceCount = selectedInstances.length;
  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => () =>
    ref.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  const viewFinding = (finding: NoticedFinding) => {
    switch (finding.kind) {
      case "time-sink":
      case "branching":
        return {
          label: t("processPanel.noticedView_step"),
          onClick: () => onSelect(finding.stepId),
        };
      case "handoffs":
        return {
          label: t("processPanel.noticedView_apps"),
          onClick: () => onOpenTab("apps"),
        };
      case "rework":
        return {
          label: t("processPanel.noticedView_cases"),
          onClick: () =>
            onOpenTab("cases", {
              // The quick "With returns" subset finds more returns than usual. This finding
              // counts every case with a return, so open its actual supporting cases instead.
              ids: selectedInstances
                .filter(instance => caseLoops(instance).count > 0)
                .map(instance => instance.id),
              label: t("processPanel.subset_rework"),
            }),
        };
      case "spread":
        return {
          label: t("processPanel.noticedView_cases"),
          onClick: () => onOpenTab("cases", { subset: "longest" }),
        };
      case "waits":
        return {
          label: t("processPanel.viewWaits"),
          onClick: () => {
            openTimeView("wait");
            scrollTo(timeRef)();
          },
        };
      default:
        return { label: t("processPanel.noticedView_usualSteps"), onClick: scrollTo(usualRef) };
    }
  };

  const hasWaits = waits.length > 0;
  const workShare = workSeconds + waitSeconds > 0 ? workSeconds / (workSeconds + waitSeconds) : 1;
  const shownWaits = showAll ? waits : waits.slice(0, TOP_ROWS);

  return (
    <>
      {process.aliases.length > 0 && (
        <p className="mb-3 text-xs leading-relaxed text-text-dim">
          {t("also_seen_as")}: {process.aliases.join(", ")}
        </p>
      )}
      {findings.length > 0 && (
        <div className="mb-4">
          <ProcessNoticedCard
            findings={findings}
            nameById={nameById}
            onAsk={onAskNoticed}
            view={viewFinding}
          />
        </div>
      )}
      <KpiTiles
        items={[
          { label: t("processPanel.metricCases"), value: instanceCount },
          {
            label: t("processPanel.metricWorkTime"),
            help: t("processPanel.metricWorkTimeHelp"),
            value: formatDuration(median(durations)),
          },
          {
            label: t("processPanel.metricStartToFinish"),
            help: t("processPanel.metricStartToFinishHelp"),
            value: elapsed.length > 0 ? formatDuration(median(elapsed)) : "—",
          },
          {
            label: t("processPanel.metricStepsPerCase"),
            title: t("steps_per_instance_full"),
            value: (instanceCount > 0 ? occurrences / instanceCount : 0).toFixed(1),
          },
        ]}
      />

      <div ref={usualRef}>
        <PanelSection
          title={t("processPanel.usualSteps")}
          description={t(
            usual.some(step => step.leadsToNext)
              ? "processPanel.usualStepsDescription"
              : "processPanel.usualStepsUnordered",
          )}
        >
          {usual.length > 0 ? (
            <UsualStepsFlow steps={usual} stepName={stepName} onSelect={onSelect} />
          ) : (
            <p className="text-sm leading-relaxed text-text-dim">
              {t("processPanel.noUsualSteps")}
            </p>
          )}
        </PanelSection>
      </div>

      {ranked.length > 0 && (
        <div ref={timeRef}>
          <PanelSection
            title={t("processPanel.whereTimeGoes")}
            description={t("processPanel.whereTimeGoesDescription")}
            action={{
              label: t("processPanel.viewAllSteps"),
              onClick: () => onOpenTab("steps"),
            }}
          >
            <TimeSplit
              workShare={workShare}
              view={timeView}
              canOpenWaits={hasWaits}
              listId={timeListId}
              onToggle={view => openTimeView(timeView === view ? null : view)}
            />
            <div id={timeListId}>
              {timeView === "work" && (
                <>
                  <p className="mb-3 mt-3 px-2 text-xs leading-relaxed text-text-dim">
                    {t("processPanel.workBreakdownDescription", {
                      total: formatDuration(workSeconds),
                    })}
                  </p>
                  <TimeReadingHeader first={t("processPanel.columnCasesWithStep")} />
                  <ul className="@container space-y-0.5">
                    {(showAll ? ranked : ranked.slice(0, TOP_ROWS)).map(node => {
                      const sharePct = Math.round((shareById.get(node.id) ?? 0) * 100);
                      return (
                        <li key={node.id}>
                          <ShareRow
                            label={stepName(node.id)}
                            title={stepName(node.id)}
                            share={shareById.get(node.id) ?? 0}
                            color={WORK_COLOR}
                            barTestId={`time-sink-bar-${node.id}`}
                            reading={
                              <TimeReadings
                                first={caseCount(node)}
                                totalSeconds={totalDuration(node)}
                                share={shareById.get(node.id) ?? 0}
                              />
                            }
                            onClick={() => onSelect(node.id)}
                            ask={{
                              label: t("ask_why_time_sink", { step: node.name }),
                              onClick: () =>
                                onAskTimeSink({ id: node.id, name: node.name }, sharePct),
                            }}
                          />
                        </li>
                      );
                    })}
                  </ul>
                  {ranked.length > TOP_ROWS && (
                    <ShowAllButton
                      expanded={showAll}
                      label={t("processPanel.showAllSteps", { count: ranked.length })}
                      onClick={() => setShowAll(shown => !shown)}
                    />
                  )}
                </>
              )}
              {timeView === "wait" && (
                <>
                  <p className="mb-3 mt-3 px-2 text-xs leading-relaxed text-text-dim">
                    {t("processPanel.waitBreakdownDescription", {
                      total: formatDuration(waitSeconds),
                    })}
                  </p>
                  <TimeReadingHeader
                    first={t("processPanel.columnAvgWait")}
                    className="pl-2 pr-10"
                  />
                  <WaitRows
                    waits={shownWaits}
                    onSelect={onSelectTransition}
                    onAsk={onAskWaitingTime}
                  />
                  {waits.length > TOP_ROWS && (
                    <ShowAllButton
                      expanded={showAll}
                      label={t("processPanel.showAllWaits", {
                        count: waits.length,
                      })}
                      onClick={() => setShowAll(shown => !shown)}
                    />
                  )}
                </>
              )}
            </div>
          </PanelSection>
        </div>
      )}
    </>
  );
};

export default ProcessOverview;
